import React, {FC, PropsWithChildren, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {ClickEventHandler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ButtonGroup} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon, IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';

/*
 * Props.
 */

export interface RecommendationBannerProps {
  iconName?: IconName;
  title: ReactNode;
  children: ReactNode;
  buttons: ReadonlyArray<ReactNode>;

  onClose?: ClickEventHandler;
  className?: string;
}

/*
 * Styles.
 */

const StyledRecommendationBanner = styled.div<{$hasClose: boolean}>`
  display: grid;

  ${(p) =>
    p.$hasClose
      ? css`
          grid-template-areas:
            'icon title close'
            '. content .'
            '. buttons .';

          grid-template-rows: min-content 1fr min-content;
          grid-template-columns: 16px 1fr 26px;
        `
      : css`
          grid-template-areas:
            'icon title'
            '. content'
            '. buttons';
          grid-template-rows: min-content 1fr;
          grid-template-columns: 16px 1fr;
        `}

  grid-gap: 6px;

  padding: 12px 12px 14px;

  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.small};

  border-radius: 12px;
  background-color: ${(p) => p.theme.palette.pink.shade10};
`;

const StyledIcon = styled(Icon)`
  grid-area: icon;
`;

const StyledTitle = styled.div`
  grid-area: title;

  font-weight: ${fontWeights.bold};
  color: ${(p) => p.theme.greys.shade90};
  line-height: 18px;
`;

const StyledContent = styled.div`
  grid-area: content;

  color: ${(p) => p.theme.greys.shade80};
  line-height: 17px;
`;

const StyledButtonGroup = styled(ButtonGroup)`
  grid-area: buttons;
  margin-top: 4px;
`;

const StyledCloseButton = styled(IconButton)`
  grid-area: close;
`;

/*
 * Component.
 */

export const RecommendationBanner: FC<PropsWithChildren<RecommendationBannerProps>> = ({
  iconName,
  title,
  children,
  buttons,
  onClose,
  className,
}) => {
  const theme = useUserTheme();
  return (
    <StyledRecommendationBanner $hasClose={Boolean(onClose)} className={className}>
      <StyledIcon name={iconName ?? 'lightbulbSmall'} color={theme.palette.pink.shade40} />

      <StyledTitle>{title}</StyledTitle>
      <StyledContent>{children}</StyledContent>
      <StyledButtonGroup>{buttons}</StyledButtonGroup>
      {onClose && (
        <StyledCloseButton
          label={<Trans id="recommendation-banner-close">Close</Trans>}
          iconName="closeSmall"
          onClick={onClose}
        />
      )}
    </StyledRecommendationBanner>
  );
};
